import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2 } from 'lucide-react';
import {
  EntityPickerBase,
  type EntityFilterCandidates,
} from '../../../../shared/components/common';
import { useIntercompanyMarkerGate } from '../../../../shared/hooks/useIntercompanyMarkerGate';
import { useOrganization, useOrganizations } from '../hooks/useOrganizations';
import { OrganizationOptionRow } from './OrganizationOptionRow';
import { OrganizationQuickCreateDialog } from './OrganizationQuickCreateDialog';
import { getEntityUrl } from '../../../../shared/utils/entityUrls';
import type { Organization } from '../types';

interface OrganizationPickerProps extends EntityFilterCandidates {
  /** Currently selected company ID */
  value: string;
  /** Callback when a company is selected */
  onChange: (organizationId: string) => void;
  /** Callback returning the selected company object (for auto-populating name) */
  onCompanySelected?: (company: Organization | null) => void;
  /** Filter companies by type (e.g., 'vendor', 'customer') */
  organizationRelationshipFilter?: string;
  /** Optional label to display above the picker */
  label?: string;
  /** Marks the label required — forwarded to EntityPickerBase's Label */
  required?: boolean;
  /** Placeholder text when no company is selected */
  placeholder?: string;
  /** Disable the picker */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Whether to show the "None" option to clear selection */
  allowClear?: boolean;
  /**
   * Idle label for the "Add new" action, naming the relationship being created
   * (e.g. "Add new vendor"). Once the user types, the standard
   * `Create "<query>"` label takes over regardless of this prop.
   */
  addNewLabel?: string;
  /** Default relationships for the quick create dialog */
  defaultRelationships?: string[];
  /** Hide the "Add new" action (useful in filter contexts) */
  hideAddNew?: boolean;
  /** Show selected company as a clickable link to its detail page */
  linkToEntity?: boolean;
  /** Pre-populate search when popover opens (e.g., cleaned transaction description) */
  initialSearch?: string;
  /** Additional CSS classes for the popover (e.g., min-width override) */
  popoverClassName?: string;
  /**
   * `id` for the combobox trigger. A picker's accessible name is its placeholder —
   * translated copy, and terminology-interpolated on top of that — so a caller that
   * needs to address this trigger from outside React (an e2e spec, a video manifest)
   * cannot do it by name in every locale. Give it a stable id instead.
   */
  triggerId?: string;
}

export function OrganizationPicker({
  value,
  onChange,
  onCompanySelected,
  organizationRelationshipFilter,
  label,
  required,
  placeholder,
  disabled,
  className,
  allowClear = false,
  addNewLabel,
  defaultRelationships,
  hideAddNew = false,
  linkToEntity = false,
  triggerId,
  initialSearch,
  popoverClassName,
  candidates,
  onShowAllCandidates,
  showAllCandidatesLabel,
}: OrganizationPickerProps) {
  const { t } = useTranslation('contacts');
  // Resolved ONCE per picker, not per option row — see useIntercompanyMarkerGate.
  const showIntercompanyMarker = useIntercompanyMarkerGate();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const prevOpenRef = useRef(false);

  // Pre-populate search when popover opens
  useEffect(() => {
    if (open && !prevOpenRef.current && initialSearch) {
      setSearch(initialSearch);
    }
    prevOpenRef.current = open;
  }, [open, initialSearch]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createDialogDomain, setCreateDialogDomain] = useState('');

  // The param is repeatable + OR'd now; this picker narrows to one role. Memoized
  // because a fresh array each render would defeat `useOrganizations`' own memo,
  // which depends on this value by reference.
  const relationshipFilter = useMemo(
    () => (organizationRelationshipFilter ? [organizationRelationshipFilter] : undefined),
    [organizationRelationshipFilter],
  );

  const { data: companiesData } = useOrganizations({
    organization_relationship: relationshipFilter,
    search: search || undefined,
    limit: 50,
  });

  const companies = useMemo(() => companiesData?.data ?? [], [companiesData?.data]);

  // The selected org can fall outside the relationship-filtered list (e.g. a
  // drop-ship PO's destination customer with no 'customer' relationship tag).
  // A picker must always render its selected value's label, so fetch the org
  // by id when the loaded list doesn't contain it and pin it to the options
  // (IMP-2). Waiting for the list avoids a spurious by-id fetch on mount.
  const selectedInList = !value || companies.some((c) => c.id === value);
  const { data: selectedOrg } = useOrganization(
    companiesData === undefined || selectedInList ? '' : value
  );
  const items = useMemo(
    () =>
      !selectedInList && !search && selectedOrg
        ? [selectedOrg, ...companies]
        : companies,
    [companies, selectedInList, search, selectedOrg]
  );

  const handleSelect = useCallback((organizationId: string) => {
    const company = items.find((c) => c.id === organizationId) ?? null;
    onChange(organizationId);
    onCompanySelected?.(company);
    setOpen(false);
    setSearch('');
  }, [items, onChange, onCompanySelected]);

  const handleClear = useCallback(() => {
    onChange('');
    onCompanySelected?.(null);
    setOpen(false);
    setSearch('');
  }, [onChange, onCompanySelected]);

  const [createDialogName, setCreateDialogName] = useState('');

  const handleAddNew = useCallback(() => {
    const searchText = search.trim();
    // If search text looks like a domain (contains a dot), use it as domain; otherwise as name
    if (searchText.includes('.')) {
      setCreateDialogDomain(searchText);
      setCreateDialogName('');
    } else {
      setCreateDialogDomain('');
      setCreateDialogName(searchText);
    }
    setOpen(false);
    setShowCreateDialog(true);
  }, [search]);

  const handleCompanyCreated = useCallback((company: Organization) => {
    onChange(company.id);
    onCompanySelected?.(company);
    setSearch('');
    setShowCreateDialog(false);
  }, [onChange, onCompanySelected]);

  // `addNewLabel` names the relationship being created ("Add new vendor") and
  // only applies to the idle state. Once the user types, every picker in the
  // app shows the same `Create "<query>"` — a caller-supplied static label must
  // not override that standard, or the row keeps reading "Add new vendor" while
  // the user is halfway through typing a name.
  const resolvedAddNewLabel = search.trim()
    ? t('companyPicker.createCompanyWithName', { name: search.trim() })
    : (addNewLabel ?? t('companyPicker.addNewCompany'));

  return (
    <>
      <EntityPickerBase<Organization>
        triggerId={triggerId}
        value={value}
        items={items}
        getItemLabel={(c) => c.name}
        renderItem={(company) => (
          <OrganizationOptionRow
            name={company.name}
            domain={company.domain}
            linkedCompanyId={company.linked_company_id}
            showIntercompanyMarker={showIntercompanyMarker}
          />
        )}
        label={label}
        required={required}
        placeholder={placeholder ?? t('companyPicker.searchOrCreate')}
        searchPlaceholder={t('companyPicker.searchCompanies')}
        emptyMessage={t('companyPicker.noCompaniesFound')}
        disabled={disabled}
        className={className}
        search={search}
        onSearchChange={setSearch}
        open={open}
        onOpenChange={setOpen}
        onSelect={handleSelect}
        allowClear={allowClear}
        onClear={handleClear}
        addNewLabel={resolvedAddNewLabel}
        onAddNew={hideAddNew ? undefined : handleAddNew}
        selectedEntityUrl={linkToEntity && value
          ? getEntityUrl({ entityType: 'companies', entityId: value })
          : undefined}
        entityIcon={linkToEntity ? Building2 : undefined}
        popoverClassName={popoverClassName}
        candidates={candidates}
        onShowAllCandidates={onShowAllCandidates}
        showAllCandidatesLabel={showAllCandidatesLabel}
      />

      <OrganizationQuickCreateDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onCreated={handleCompanyCreated}
        skipNavigation
        defaultRelationships={defaultRelationships}
        defaultLookup={createDialogDomain}
        defaultName={createDialogName}
      />
    </>
  );
}
